import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  Account,
  AccountCreate,
  Connection,
  ConsentUrl,
  Holding,
  HoldingCreate,
  HistorySnapshot,
  ImportResult,
  PortfolioSummary,
  Profile,
  Transaction,
  TransactionCreate,
  UserPortfolio,
  UserPortfolioCreate,
  UserPortfolioUpdate,
  WatchlistItemCreate,
  WatchlistItemWithPrice,
  TaxYearSummary,
} from './models';

/**
 * Typed client for the Vestorise portfolio API. The Firebase ID token is added
 * by `authInterceptor`, so methods here stay concerned only with endpoints.
 */
@Injectable({ providedIn: 'root' })
export class PortfolioApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/portfolio`;
  private readonly revolutBase = `${environment.apiBaseUrl}/integrations/revolut`;

  getSummary(portfolioId?: string): Observable<PortfolioSummary> {
    const options =
      portfolioId && portfolioId !== 'ALL' ? { params: { portfolio_id: portfolioId } } : {};
    return this.http.get<PortfolioSummary>(`${this.base}/summary`, options);
  }

  getHistory(limit?: number): Observable<HistorySnapshot[]> {
    const options = limit ? { params: { limit } } : {};
    return this.http.get<HistorySnapshot[]>(`${this.base}/history`, options);
  }

  getPortfolios(): Observable<UserPortfolio[]> {
    return this.http.get<UserPortfolio[]>(`${this.base}/portfolios`);
  }

  createPortfolio(data: UserPortfolioCreate): Observable<UserPortfolio> {
    return this.http.post<UserPortfolio>(`${this.base}/portfolios`, data);
  }

  updatePortfolio(id: string, data: UserPortfolioUpdate): Observable<UserPortfolio> {
    return this.http.put<UserPortfolio>(`${this.base}/portfolios/${id}`, data);
  }

  deletePortfolio(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/portfolios/${id}`);
  }

  listAccounts(): Observable<Account[]> {
    return this.http.get<Account[]>(`${this.base}/accounts`);
  }

  createAccount(data: AccountCreate): Observable<Account> {
    return this.http.post<Account>(`${this.base}/accounts`, data);
  }

  updateAccount(id: string, data: AccountCreate): Observable<Account> {
    return this.http.put<Account>(`${this.base}/accounts/${id}`, data);
  }

  deleteAccount(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/accounts/${id}`);
  }

  listHoldings(): Observable<Holding[]> {
    return this.http.get<Holding[]>(`${this.base}/holdings`);
  }

  createHolding(data: HoldingCreate): Observable<Holding> {
    return this.http.post<Holding>(`${this.base}/holdings`, data);
  }

  updateHolding(id: string, data: HoldingCreate): Observable<Holding> {
    return this.http.put<Holding>(`${this.base}/holdings/${id}`, data);
  }

  deleteHolding(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/holdings/${id}`);
  }

  listTransactions(): Observable<Transaction[]> {
    return this.http.get<Transaction[]>(`${this.base}/transactions`);
  }

  createTransaction(data: TransactionCreate): Observable<Transaction> {
    return this.http.post<Transaction>(`${this.base}/transactions`, data);
  }

  /**
   * Upload a Revolut / Trading 212 / Morgan Stanley CSV export into the given
   * account. `ticker` is needed only for Morgan Stanley GSU exports, which
   * don't name the stock themselves.
   */
  importCsv(accountId: string, file: File, ticker?: string): Observable<ImportResult> {
    const form = new FormData();
    form.append('accountId', accountId);
    form.append('file', file);
    if (ticker) {
      form.append('ticker', ticker);
    }
    return this.http.post<ImportResult>(`${this.base}/import/csv`, form);
  }

  getProfile(): Observable<Profile> {
    return this.http.get<Profile>(`${this.base}/profile`);
  }

  updateProfile(data: Profile): Observable<Profile> {
    return this.http.put<Profile>(`${this.base}/profile`, data);
  }

  /** The user's Revolut connection, or `null` when not linked. */
  getRevolutConnection(): Observable<Connection | null> {
    return this.http.get<Connection | null>(`${this.revolutBase}/status`);
  }

  /** Start the OAuth flow — returns the consent URL to send the user to. */
  connectRevolut(): Observable<ConsentUrl> {
    return this.http.post<ConsentUrl>(`${this.revolutBase}/connect`, {});
  }

  disconnectRevolut(): Observable<void> {
    return this.http.delete<void>(`${this.revolutBase}/`);
  }

  getWatchlist(): Observable<WatchlistItemWithPrice[]> {
    return this.http.get<WatchlistItemWithPrice[]>(`${this.base}/watchlist`);
  }

  addWatchlistItem(data: WatchlistItemCreate): Observable<WatchlistItemWithPrice> {
    return this.http.post<WatchlistItemWithPrice>(`${this.base}/watchlist`, data);
  }

  deleteWatchlistItem(ticker: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/watchlist/${ticker}`);
  }

  refreshWatchlistPrices(): Observable<WatchlistItemWithPrice[]> {
    return this.http.post<WatchlistItemWithPrice[]>(`${this.base}/watchlist/refresh`, {});
  }

  getRealizedGains(year?: string, portfolioId?: string): Observable<TaxYearSummary> {
    const params: Record<string, string> = {};
    if (year && year !== 'ALL') {
      params['year'] = year;
    }
    if (portfolioId && portfolioId !== 'ALL') {
      params['portfolio_id'] = portfolioId;
    }
    return this.http.get<TaxYearSummary>(`${this.base}/tax/realized-gains`, { params });
  }
}
